import { useActiveForm } from 'pages/claims/components';
import React, { useEffect, useRef, useState } from 'react';
import { getValueByPath } from 'utils/getValueByPath';
import { DatePicker, DateTimePicker, TimePicker } from '@mantine/dates';
import { useHotkeys } from '@mantine/hooks';
import {
  ActionIcon,
  CheckIcon,
  CloseButton,
  Group,
  Popover,
  Stack,
  TextInput,
  useComputedColorScheme,
} from '@mantine/core';
import { IconCalendar } from '@tabler/icons-react';
import { useCurrentSiteStore } from 'store/SiteStore';
import {
  formatDateTimeEditorValue,
  formatDateTimeInputValue,
  formatTimeInput,
  nowISO,
  parseDateTime,
  parseDateTimeEditorValue,
  parseDateTimeInputValue,
  parseTimeInput,
} from 'utils/dateUtils';
import { useTranslation } from '@refinedev/core';

interface ModalDateTimeInputProps {
  label: string;
  accessorKey: string;
  width?: number | string;
  hotkey?: string;
  required?: boolean;
  disabled?: boolean;
}

export const ModalDateTimeInput: React.FC<ModalDateTimeInputProps> = React.memo(
  ({
    label,
    accessorKey,
    hotkey,
    width = '100%',
    required = false,
    disabled = false,
  }) => {
    const { form, prefix, formType } = useActiveForm();
    const { timezone } = useCurrentSiteStore();
    const { translate } = useTranslation();
    const computedColorScheme = useComputedColorScheme('dark');
    const color = computedColorScheme === 'dark' ? 'white' : 'black';
    const path = prefix ? `${prefix}.${accessorKey}` : accessorKey;
    const formValues = form.getValues();
    const storedValue = getValueByPath(formValues, path) as string | undefined;
    const shouldDefaultToNow = formType !== 'bulkEdit';

    const getDateValue = (value?: string) =>
      value || shouldDefaultToNow
        ? formatDateTimeInputValue(value || nowISO(timezone), timezone)
        : null;

    const [dateValue, setDateValue] = useState<string | null>(() =>
      getDateValue(storedValue)
    );
    const [textValue, setTextValue] = useState(() =>
      storedValue || shouldDefaultToNow
        ? formatDateTimeEditorValue(storedValue || nowISO(timezone), timezone)
        : ''
    );
    const [committedDateTimeValue, setCommittedDateTimeValue] = useState(
      () => storedValue || (shouldDefaultToNow ? nowISO(timezone) : '')
    );
    const [opened, setOpened] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    useEffect(() => {
      setDateValue(getDateValue(storedValue));
      setCommittedDateTimeValue(
        storedValue || (shouldDefaultToNow ? nowISO(timezone) : '')
      );
      setTextValue(
        storedValue || shouldDefaultToNow
          ? formatDateTimeEditorValue(storedValue || nowISO(timezone), timezone)
          : ''
      );
      setLocalError(null);
    }, [storedValue, shouldDefaultToNow, timezone]);

    const error = getValueByPath(form.errors, path);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const isEditableCreateForm = formType !== 'edit';

    const clearError = () => {
      setLocalError(null);
      form.clearFieldError(path);
    };

    const setCommittedValue = (nextValue: ReturnType<typeof parseDateTime>) => {
      if (!nextValue.isValid) return;

      const isoValue = nextValue.toISO();
      const formattedDateValue = formatDateTimeInputValue(nextValue, timezone);

      form.setFieldValue(path, isoValue);
      setCommittedDateTimeValue(isoValue);
      setDateValue(formattedDateValue);
      setTextValue(formatDateTimeEditorValue(nextValue, timezone));
      clearError();

      if (path.includes('serviceStart')) {
        form.setFieldValue(
          path.replace('serviceStart', 'serviceEnd'),
          nextValue.plus({ minutes: 15 }).toISO()
        );
      }
    };

    const committedValue = () =>
      committedDateTimeValue || storedValue || nowISO(timezone);

    const commitTextValue = () => {
      if (!textValue.trim()) {
        setDateValue(null);
        setCommittedDateTimeValue('');
        setLocalError(null);
        form.setFieldValue(path, undefined);
        form.clearFieldError(path);
        return true;
      }

      const parsed = parseDateTimeEditorValue(
        textValue,
        committedValue(),
        timezone
      );

      if (!parsed.isValid) {
        const message = translate(
          'pages:claims.dateTimeInput.invalidFormat',
          'Use YYYY-MM-DD or YYYY MM DD, with optional hh:mm AM/PM.'
        );
        setLocalError(message);
        form.setFieldError(path, message);
        return false;
      }

      setCommittedValue(parsed);
      return true;
    };

    const handleDateChange = (value: string | null) => {
      if (!value) return;
      const parsed = parseDateTimeEditorValue(
        value,
        committedValue(),
        timezone
      );
      setCommittedValue(parsed);
    };

    const handleTimeChange = (value: string) => {
      const parsedTime = parseTimeInput(value);
      const parsedDate = parseDateTime(committedValue(), { zone: timezone });
      if (!parsedTime.isValid || !parsedDate.isValid) return;

      setCommittedValue(
        parsedDate.set({
          hour: parsedTime.hour,
          minute: parsedTime.minute,
          second: 0,
          millisecond: 0,
        })
      );
    };

    const clearValue = () => {
      setTextValue('');
      setDateValue(null);
      setCommittedDateTimeValue('');
      setOpened(false);
      setLocalError(null);
      form.setFieldValue(path, undefined);
      form.clearFieldError(path);
      inputRef.current?.focus();
    };

    useHotkeys([
      [
        hotkey || '',
        (event) => {
          event.preventDefault();
          if (isEditableCreateForm) {
            inputRef.current?.focus();
            setOpened(true);
          } else if (buttonRef.current) {
            buttonRef.current.focus();
            buttonRef.current.click();
          }
        },
      ],
    ]);

    if (isEditableCreateForm) {
      return (
        <Popover
          opened={opened && !disabled}
          onChange={setOpened}
          position="bottom-start"
          withinPortal
          trapFocus={false}
        >
          <Popover.Target>
            <TextInput
              ref={inputRef}
              label={label}
              value={textValue}
              onChange={(event) => {
                setTextValue(event.currentTarget.value);
                if (localError) clearError();
              }}
              onClick={() => !disabled && setOpened(true)}
              onBlur={() => {
                commitTextValue();
                requestAnimationFrame(() => {
                  if (!dropdownRef.current?.contains(document.activeElement)) {
                    setOpened(false);
                  }
                });
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  if (commitTextValue()) setOpened(false);
                } else if (event.key === 'Escape') {
                  setTextValue(
                    formatDateTimeEditorValue(committedValue(), timezone)
                  );
                  clearError();
                  setOpened(false);
                }
              }}
              rightSection={
                <Group gap={2} wrap="nowrap">
                  {textValue && !disabled && (
                    <CloseButton
                      size="sm"
                      tabIndex={-1}
                      aria-label={translate(
                        'pages:claims.dateTimeInput.clear',
                        'Clear date and time'
                      )}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={clearValue}
                    />
                  )}
                  <ActionIcon
                    variant="subtle"
                    tabIndex={-1}
                    disabled={disabled}
                    aria-label={translate(
                      'pages:claims.dateTimeInput.openPicker',
                      'Open date and time picker'
                    )}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setOpened((value) => !value)}
                  >
                    <IconCalendar size={18} />
                  </ActionIcon>
                </Group>
              }
              rightSectionPointerEvents="all"
              rightSectionWidth={textValue && !disabled ? 68 : 38}
              placeholder="YYYY-MM-DD hh:mm AM/PM"
              error={localError || error}
              withAsterisk={required}
              radius="md"
              size="md"
              w={width}
              disabled={disabled}
              c={color}
            />
          </Popover.Target>
          <Popover.Dropdown ref={dropdownRef}>
            <Stack gap="sm">
              <DatePicker
                value={dateValue}
                onChange={handleDateChange}
                defaultDate={dateValue || undefined}
              />
              <Group gap="xs" wrap="nowrap">
                <TimePicker
                  value={formatTimeInput(committedValue(), timezone)}
                  onChange={handleTimeChange}
                  format="24h"
                  flex={1}
                />
                <ActionIcon
                  variant="default"
                  size="input-sm"
                  aria-label={translate(
                    'pages:claims.dateTimeInput.closePicker',
                    'Close date and time picker'
                  )}
                  onClick={() => {
                    setOpened(false);
                    requestAnimationFrame(() => inputRef.current?.focus());
                  }}
                >
                  <CheckIcon size="30%" />
                </ActionIcon>
              </Group>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      );
    }

    return (
      <DateTimePicker
        label={label}
        ref={buttonRef}
        value={dateValue}
        c={color}
        onChange={(newDate) => {
          if (disabled) return; // ignore changes when disabled
          if (!newDate) {
            setDateValue(null);
            form.setFieldValue(path, undefined);
            return;
          }
          setDateValue(newDate);
          const serviceStart = parseDateTimeInputValue(newDate, timezone);
          if (!serviceStart.isValid) return;

          const serviceStartISOWithOffset = serviceStart.toISO();
          form.setFieldValue(path, serviceStartISOWithOffset);
          if (path.includes('serviceStart')) {
            const endISO = serviceStart.plus({ minutes: 15 }).toISO();
            form.setFieldValue(
              path.replace('serviceStart', 'serviceEnd'),
              endISO
            );
          }
        }}
        popoverProps={{
          withinPortal: true,
          onClose: () => {
            requestAnimationFrame(() => buttonRef.current?.focus());
          },
        }}
        error={error}
        onError={(evt) => {
          form.setFieldError(accessorKey, evt.currentTarget.value);
        }}
        clearable
        valueFormat="YYYY-MM-DD hh:mm A"
        key={path}
        withAsterisk={required}
        radius="md"
        size="md"
        w={width}
        disabled={disabled}
      />
    );
  }
);
